import { Injectable } from '@angular/core';

export interface IframeContext {
  isInIframe: boolean;
  isThirdParty: boolean;
  parentOrigin: string | null;
  currentOrigin: string;
  referrer: string;
}

@Injectable({
  providedIn: 'root'
})
export class IframeDetectionService {

  private iframeContext: IframeContext;

  constructor() {
    this.iframeContext = this.detectIframeContext();
    this.storeIframeData();
  }

  /**
   * Detect comprehensive iframe context
   */
  private detectIframeContext(): IframeContext {
    const isInIframe = this.isInsideIframe();
    const isThirdParty = this.isThirdPartyIframe();
    const parentOrigin = this.getParentOrigin();
    const currentOrigin = window.location.origin;
    const referrer = document.referrer;

    return {
      isInIframe,
      isThirdParty,
      parentOrigin,
      currentOrigin,
      referrer
    };
  }

  /**
   * Store iframe detection data in localStorage
   */
  private storeIframeData(): void {
    localStorage.setItem('iframeContext', JSON.stringify(this.iframeContext));
    localStorage.setItem('isInIframe', JSON.stringify(this.iframeContext.isInIframe));
    localStorage.setItem('isThirdParty', JSON.stringify(this.iframeContext.isThirdParty));

    if (this.iframeContext.parentOrigin) {
      localStorage.setItem('parentOrigin', this.iframeContext.parentOrigin);
    }

    if (this.iframeContext.referrer) {
      localStorage.setItem('referrer', this.iframeContext.referrer);
    }
  }

  /**
   * Check if the application is running inside an iframe
   */
  public isInsideIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to cross-origin restrictions,
      // we're definitely in an iframe from a different domain
      return true;
    }
  }

  /**
   * Check if the iframe is from a third-party domain
   */
  public isThirdPartyIframe(): boolean {
    if (!this.isInsideIframe()) {
      return false;
    }

    try {
      // Try to access parent window location
      const parentOrigin = window.parent.location.origin;
      const currentOrigin = window.location.origin;

      return parentOrigin !== currentOrigin;
    } catch (e) {
      // Cross-origin restriction means it's definitely third-party
      return true;
    }
  }

  /**
   * Get the parent window origin (if accessible)
   */
  public getParentOrigin(): string | null {
    try {
      if (this.isInsideIframe()) {
        return window.parent.location.origin;
      }
      return window.location.origin;
    } catch (e) {
      // Can't access due to cross-origin restrictions
      // Try to extract from referrer
      if (document.referrer) {
        try {
          return new URL(document.referrer).origin;
        } catch (urlError) {
          return null;
        }
      }
      return null;
    }
  }

  /**
   * Get stored iframe context from localStorage
   */
  public getStoredIframeContext(): IframeContext | null {
    const stored = localStorage.getItem('iframeContext');
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Get current iframe context
   */
  public getIframeContext(): IframeContext {
    return this.iframeContext;
  }

  /**
   * Check if current session is from third-party
   */
  public isFromThirdParty(): boolean {
    return this.iframeContext.isThirdParty;
  }

  /**
   * Check if current session is in iframe
   */
  public isInIframe(): boolean {
    return this.iframeContext.isInIframe;
  }

  /**
   * Get parent domain information
   */
  public getParentDomain(): string | null {
    if (this.iframeContext.parentOrigin) {
      try {
        return new URL(this.iframeContext.parentOrigin).hostname;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /**
   * Clear iframe detection data
   */
  public clearIframeData(): void {
    localStorage.removeItem('iframeContext');
    localStorage.removeItem('isInIframe');
    localStorage.removeItem('isThirdParty');
    localStorage.removeItem('parentOrigin');
    localStorage.removeItem('referrer');
  }
}
